import {
    useSensors,
    useSensor,
    PointerSensor,
    KeyboardSensor,
    DragEndEvent,
    DndContext,
    closestCenter,
    UniqueIdentifier,
} from "@dnd-kit/core";
import {
    sortableKeyboardCoordinates,
    arrayMove,
    SortableContext,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import type { ReactNode } from "react";
import { SortableRow } from "./SortableRow";

type DraggableRowsProps<Data extends { id: UniqueIdentifier }> = {
    items: Data[];
    renderItem: (item: Data, draggableItem: ReactNode, index: number) => ReactNode;
    onDragEnd: (newItems: Data[], movedItem: Data) => void;
    disabled?: boolean;
    getHandleLabel?: (item: Data) => string;
};
export const SortableRows = <Data extends { id: UniqueIdentifier }>(props: DraggableRowsProps<Data>) => {
    const { items, renderItem, onDragEnd, disabled = false, getHandleLabel } = props;

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8, // Require 8px of movement before activating
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (!over || active.id === over.id) {
            return;
        }
        const activeIndex = items.findIndex((i) => i.id === active.id);
        const overIndex = items.findIndex((i) => i.id === over.id);
        if (activeIndex === -1 || overIndex === -1) {
            return;
        }

        const newOrder = arrayMove(items, activeIndex, overIndex);
        onDragEnd(newOrder, items[activeIndex]);
    };

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={items} strategy={verticalListSortingStrategy}>
                {items.map((v, index) => {
                    return (
                        <SortableRow
                            key={v.id}
                            item={v}
                            renderItem={renderItem}
                            index={index}
                            disabled={disabled}
                            handleLabel={getHandleLabel?.(v)}
                        />
                    );
                })}
            </SortableContext>
        </DndContext>
    );
};
